import {
  Box, Typography,
  useTheme
} from "@mui/material";
import { getHours, getMinutes } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CardGrid,
  CardBodyGrid,
  InfoTypography,
  SeatsTypography
} from "./CardEdition.styles";
import { CardEditionProps } from "./CardEdition.types";
import {
  Icon,
  Link, Modal, StyledBoxContact
} from "../../components";
import { ModalSessionDetail } from "../../components/ModalSessionDetail";
import { LEVEL, STATUS_LO } from "../../consts";
import { useEnrollmentPollingContext } from "../../context/EnrollmentPollingContext";
import {
  useEditionSessionsQuery,
  useEnrollmentMutation,
  useEnrollmentStatusQuery,
  useInvalidateEditionsQuery,
  useInvalidateLearningObjectQuery,
  usePatchMutation
} from "../../queries";
import { formatDate } from "../../services";
import { useAuthStore } from "../../stores";
import type { SessionEdition, StatusLO } from "../../types";
import { formatSessionOccupiedError, isSessionOccupiedError } from "../../utils/editionSession";
import { getError } from "../../utils/error";
import { getLearningObjectType } from "../../utils/general";
import { ButtonDigited } from "../Button/Button.variants";
import { UnsubscribeEditionConfirmModal } from "../UnsubscribeEditionConfirmModal";

// Polling storage utilities
const POLLING_STORAGE_KEY = "enrollment_polling";

export interface PollingData {
  editionId: number;
  startTime: number;
  userId: number;
}

const savePollingToStorage = (editionId: number, userId: number) => {
  sessionStorage.setItem(POLLING_STORAGE_KEY, JSON.stringify({
    editionId,
    startTime: Date.now(),
    userId
  }));
};

const getPollingFromStorage = (): PollingData | null => {
  const data = sessionStorage.getItem(POLLING_STORAGE_KEY);
  return data ? JSON.parse(data) : null;
};

const clearPollingFromStorage = () => {
  sessionStorage.removeItem(POLLING_STORAGE_KEY);
};

// Pending approval storage utilities
const PENDING_APPROVAL_STORAGE_KEY = "enrollment_pending_approval";

interface PendingApprovalData {
  editionId: number;
  userId: number;
}

const savePendingApprovalToStorage = (editionId: number, userId: number) => {
  const existing = getPendingApprovalsFromStorage();
  if (!existing.some(item => item.editionId === editionId && item.userId === userId)) {
    existing.push({ editionId, userId });
    sessionStorage.setItem(PENDING_APPROVAL_STORAGE_KEY, JSON.stringify(existing));
  }
};

const getPendingApprovalsFromStorage = (): PendingApprovalData[] => {
  const data = sessionStorage.getItem(PENDING_APPROVAL_STORAGE_KEY);
  return data ? JSON.parse(data) : [];
};

const isEditionPendingApproval = (editionId: number, userId: number): boolean => {
  return getPendingApprovalsFromStorage().some(item => item.editionId === editionId && item.userId === userId);
};

const clearPendingApprovalFromStorage = (editionId: number, userId: number) => {
  const existing = getPendingApprovalsFromStorage();
  const filtered = existing.filter(item => !(item.editionId === editionId && item.userId === userId));
  sessionStorage.setItem(PENDING_APPROVAL_STORAGE_KEY, JSON.stringify(filtered));
};


export function CardEdition({
  booked,
  bookedSeats,
  editionId,
  endTime,
  extraInfo,
  enrollType,
  enrollmentStatus,
  // gMapsUrl,
  hasEditionEnrolled,
  hasEditionEnrolledAbsent,
  id,
  isAbsent,
  learningObjectTypology,
  //link,
  // location,
  maxSeats,
  // rooms,
  courseId,
  rootId,
  startTime,
  status,
  statusEdition,
  selfEnrollment,
  currentLO,
  attributesIds,
  enrollableEditionsWithSessions,
  contentEditions,
  isPollingEditionChange,
  onChangeEditionClick,
  ...props
}: CardEditionProps) {
  const theme = useTheme();
  const { i18n, t } = useTranslation();
  const today = new Date().getTime();
  const [bannerVisible, setBannerVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalText, setModalText] = useState("");
  const [bannerText, setBannerText] = useState("");
  const [showSessionDetails, setShowSessionDetails] = useState(false);
  const [showUnsubscribeConfirm, setShowUnsubscribeConfirm] = useState(false);
  const [isUnsubscribing, setIsUnsubscribing] = useState(false);
  const { data: sessions } = useEditionSessionsQuery({
    editionId: editionId?.toString() ?? "",
    type: learningObjectTypology
  });
  const [nextSession, setNextSession] = useState<SessionEdition | null>(null);
  const [isCurrentSession, setIsCurrentSession] = useState<boolean>(false);
  const [showInProgressStatus, setShowInProgressStatus] = useState(false);
  const [isPolling, setIsPolling] = useState(false);

  // Ref per salvare gli ID dei timer del polling
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingCompletedRef = useRef<boolean>(false);

  // Global context to disable all buttons during polling
  const { isAnyPolling, setPollingState } = useEnrollmentPollingContext();

  // User data for status check
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? 0;
  const userId = useAuthStore(state => state.userData?.id_user) ?? 0;

  // Restore polling from sessionStorage on mount and clean up pending approvals
  useEffect(() => {
    const savedPolling = getPollingFromStorage();
    if (savedPolling && savedPolling.editionId === editionId && savedPolling.userId === userId) {
      if (statusEdition !== "E" && statusEdition !== "R") {
        setShowInProgressStatus(true);
        setIsPolling(true);
        setPollingState(true);
      } else {
        // Clear storage if enrollment is already complete or rejected
        clearPollingFromStorage();
      }
    }

    // Clear pending approval if enrollment is complete or rejected
    if (editionId && userId && (status === "E" || status === "R")) {
      clearPendingApprovalFromStorage(editionId, userId);
    }
  }, [editionId, setPollingState, status, statusEdition, userId]);

  // const isRejected = statusEdition === "R" || currentLO.status === "R";
  const isBookable = (!booked && !hasEditionEnrolled && !isAbsent) && (
    status === "N"
    || status === "T"
    || status === "R"
    || status === "E"
  );

  const isCompleted = statusEdition === "C";

  const { invalidateLearningObject } = useInvalidateLearningObjectQuery({
    courseId:courseId,
    id:id,
    learningObjectTypology:learningObjectTypology,
    rootId:rootId
  });
  const { invalidateEditions } = useInvalidateEditionsQuery({
    classroomId:id.toString()
  });

  // Check enrollment status at mount only if statusEdition === "N" OR if polling is active
  const {
    data: enrollmentStatusData,
    refetch: refetchEnrollmentStatus
  } = useEnrollmentStatusQuery({
    corporateId,
    editionId: editionId ?? 0,
    enabled: false, // Disabled, use manual refetch only
    userId
  });

  // Polling logic with progressive intervals
  useEffect(() => {
    if (!isPolling) {
      // Clean up timers when polling stops
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      return;
    }

    const startTime = Date.now();
    const MAX_POLLING_DURATION = 120000; // 2 minutes
    const MAX_INTERVAL = 20000; // 20 seconds
    let currentInterval = 5000; // Start with 5 seconds

    const scheduleNextPoll = () => {
      const elapsed = Date.now() - startTime;

      if (elapsed >= MAX_POLLING_DURATION) {
        // Timeout reached
        pollingCompletedRef.current = true;
        setIsPolling(false);
        setShowInProgressStatus(false);
        setPollingState(false);
        clearPollingFromStorage();
        setBannerText(t("enrollment-polling-error"));
        setBannerVisible(true);
        invalidateLearningObject();
        invalidateEditions();
        return;
      }

      // Refetch status
      refetchEnrollmentStatus();

      // Increment interval by 5s if below max
      if (currentInterval < MAX_INTERVAL) {
        currentInterval += 5000;
      }

      // Schedule next poll
      pollingTimeoutRef.current = setTimeout(scheduleNextPoll, currentInterval);
    };

    // First call after initial interval (5s)
    pollingTimeoutRef.current = setTimeout(scheduleNextPoll, currentInterval);

    return () => {
      if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    };
  }, [isPolling, refetchEnrollmentStatus, invalidateEditions, invalidateLearningObject, setPollingState, t]);

  // Handle enrollment status response
  useEffect(() => {
    if (!enrollmentStatusData?.status) return;

    // If polling is already completed, do nothing
    if (pollingCompletedRef.current) return;

    // Only process status changes if polling is active
    // This prevents showing the modal when returning to the tab
    if (!isPolling) return;

    const currentStatus = enrollmentStatusData.status;

    if (currentStatus === "IN_PROGRESS") {
      // Already polling, continue
      setShowInProgressStatus(true);
    } else if (currentStatus === "COMPLETE" || currentStatus === "ERROR") {
      // Mark polling as completed FIRST to avoid loops
      pollingCompletedRef.current = true;
      if (showModalEditionEnrollSuccess) setShowModalEditionEnrollSuccess(false);

      // Stop polling
      setIsPolling(false);
      setShowInProgressStatus(false);
      setPollingState(false);

      // Remove from sessionStorage
      clearPollingFromStorage();

      // Show modal with polling result
      if (currentStatus === "COMPLETE") {
        setPollingResultMessage(t("enrollment-complete-success"));
        setShowModalPollingResult(true);
      } else {
        // ERROR
        setPollingResultMessage(t("max-capacity-reached-error"));
        setShowModalPollingResult(true);
      }
      // invalidateEditions() is called in modal onClose to avoid re-render closing the modal
    }
    // NOT_ENROLLED: do nothing, normal behavior
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollmentStatusData?.status, isPolling]);

  const parseUtcFallbackTime = (value: string | number) => {
    if (typeof value === "number") {
      return value;
    }

    // Fallback assumption: edition start/end times from `/editions` are UTC values
    // serialized without timezone information, so we force `Z` during parsing.
    const normalizedValue = value.includes("T") ? value : value.replace(" ", "T");
    return new Date(normalizedValue.endsWith("Z") ? normalizedValue : `${normalizedValue}Z`).getTime();
  };

  const seatsAvailable = maxSeats - bookedSeats > 0;
  const hasEnrollableEditions = enrollableEditionsWithSessions && enrollableEditionsWithSessions.length > 0;
  const hasAnyEditionPendingApproval = contentEditions?.some(edition =>
    edition.status === "W" || isEditionPendingApproval(edition.id, userId)
  ) || false;
  const newStartDate = new Date(startTime);
  const newEndDate = new Date(endTime);
  const startEdition = formatDate(newStartDate, i18n.language)?.replaceAll("/","-");
  const endEdition = formatDate(newEndDate, i18n.language)?.replaceAll("/","-");
  const editionStartTime = parseUtcFallbackTime(startTime);
  const editionEndTime = parseUtcFallbackTime(endTime);
  const sessionStartTime = Number(nextSession?.start_date);
  const sessionEndTime = Number(nextSession?.end_date);
  const hasSessionWindow = Number.isFinite(sessionStartTime) && Number.isFinite(sessionEndTime);
  const meetingStartTime = hasSessionWindow ? sessionStartTime : editionStartTime;
  const meetingEndTime = hasSessionWindow ? sessionEndTime : editionEndTime;

  const idEnrollment = rootId ?
    rootId : courseId ?
      courseId : id;

  const typologyEnrollment = rootId ?
    LEVEL.PATH : courseId ?
      LEVEL.COURSE :
      getLearningObjectType(learningObjectTypology);

  const currentDay =
    booked && (
      (today >= meetingStartTime - (2 * 60 * 60 * 1000)) &&
      (today <= meetingEndTime)
    );

  const roomsString = nextSession?.rooms && nextSession?.rooms.length > 0 ? nextSession?.rooms.toString().replaceAll(","," - ") : null;

  const {
    mutate: addEnrollmentMutation,
    error: enrollmentMutationError,
    isError: enrollmentMutationIsError,
    isSuccess: enrollmentMutationHadSuccess,
    isPending:enrollmentIsLoading
  } = useEnrollmentMutation({
    enrollType:enrollType,
    learningObjectId: idEnrollment,
    learningObjectTypology:typologyEnrollment,
    attributesIds:attributesIds
  });


  const {
    mutate: addEnrollmentEditionMutation,
    error: enrollmentEditionMutationError,
    isError: enrollmentEditionMutationIsError,
    isSuccess: enrollmentEditionMutationHadSuccess,
    isPending:enrollmentEditionIsLoading
  } = usePatchMutation({
    editionId:editionId,
    learningObjectId: id,
    learningObjectTypology:learningObjectTypology,
    isToj: !!currentLO.tojEditionIds
  });

  useEffect(()=> {
    if(enrollmentMutationHadSuccess){
      addEnrollmentEditionMutation(undefined, {});
    }
  },[
    addEnrollmentEditionMutation,
    enrollmentIsLoading,
    enrollmentMutationError,
    enrollmentMutationHadSuccess,
    enrollmentMutationIsError
  ]);

  const [showModalEditionEnrollSuccess, setShowModalEditionEnrollSuccess] = useState(false);
  const [showModalPollingResult, setShowModalPollingResult] = useState(false);
  const [pollingResultMessage, setPollingResultMessage] = useState("");

  const requestSentModalShownRef = useRef(false);

  useEffect(()=> {
    if (enrollmentEditionMutationHadSuccess) {
      if (enrollType === "REQUESTED_AUTO_ENROLL") {
        // Enrollment with approval: show modal only once, no polling
        if (!requestSentModalShownRef.current) {
          requestSentModalShownRef.current = true;
          if (editionId) savePendingApprovalToStorage(editionId, userId);
          setShowModalEditionEnrollSuccess(true);
        }
      } else {
        // Auto enrollment: start polling, show modal only once
        if (!requestSentModalShownRef.current) {
          requestSentModalShownRef.current = true;
          pollingCompletedRef.current = false;
          if (editionId) savePollingToStorage(editionId, userId);
          setShowInProgressStatus(true);
          setIsPolling(true);
          setPollingState(true);
          setShowModalEditionEnrollSuccess(true);
        }
      }
    }
  }, [
    enrollmentEditionMutationHadSuccess,
    setPollingState,
    editionId,
    enrollType,
    invalidateEditions,
    invalidateLearningObject,
    userId
  ]);

  useEffect(()=> {
    if(
      (enrollmentMutationIsError && enrollmentMutationError) ||
      (enrollmentEditionMutationIsError && enrollmentEditionMutationError)
    ){

      const error = getError(enrollmentEditionMutationError);

      const maximumError = error?.[0]?.message.includes("participants maximum number");
      const alreadyAssociatedError = error?.[0]?.message.includes("already associated with user");
      const maxCapacityReached = error?.[0]?.message.includes("MAX_EDITION_CAPACITY_REACHED_DURING_UPDATE_ENROLLMENTS");

      if (maxCapacityReached) {
        setShowModal(true);
        setModalText(t("max-capacity-reached-error"));
        invalidateEditions();
        return;
      }

      if (error?.[0]?.name === "FATAL") {
        setShowModal(true);

        const errorKey = JSON.parse(
          enrollmentEditionMutationError as unknown as string
        )?.errors["FATAL"]?.[0]?.key;
        if(errorKey === "bad.request.user.already.associated"){
          setModalText(t("already-associated-error"));
        } else if (
          isSessionOccupiedError(error?.[0]?.message)
        ) {
          setModalText(
            formatSessionOccupiedError(error[0].message, t)
          );
        } else{
          setModalText(error?.[0]?.message || t("generic_error"));
        }

        invalidateEditions();
        return;
      }

      setBannerVisible(true);
      if (maximumError) {
        setBannerText(t("maximum-participants-error"));
      } else if (alreadyAssociatedError) {
        setBannerText(t("already-associated-error"));
      } else {
        setBannerText(t("enrollment-generic-error"));
      }

      invalidateEditions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[
    enrollmentEditionIsLoading,
    enrollmentEditionMutationError,
    enrollmentEditionMutationHadSuccess,
    enrollmentEditionMutationIsError,
    enrollmentIsLoading,
    enrollmentMutationError,
    enrollmentMutationHadSuccess,
    enrollmentMutationIsError]);

  const getButtonLabel = () => {
    if (isAbsent) {
      return t("absent");
    }
    if (isCompleted){
      return t("completed");
    }
    if (showInProgressStatus) {
      return t("enrollment-in-progress");
    } else if (isPollingEditionChange) {
      return t("edition_change_in_progress");
    } else if ((enrollType === "TOP_DOWN" || enrollType === "TOP_MANDATORY" || currentLO?.enrollmentEnrollType.includes("TOP")) && statusEdition === STATUS_LO.E) {
      return t("enrolled");
    } else if (statusEdition === STATUS_LO.E) {
      return t("change_edition");
    } else if (enrollmentStatus === "PA" || (hasEditionEnrolledAbsent && enrollType === "AUTO_ENROLL")) {
      return t("subscribe");
    } else if (editionId && (
      isEditionPendingApproval(editionId, userId) || statusEdition === STATUS_LO.W)) {
      return t("waiting-for-confirmation");
    } else if (statusEdition === STATUS_LO.W) {
      return t("validation_text");
    } else {
      return t("register_request");
    }
  };

  // const getArrayString = (array: {
  //   id: string | number,
  //   completeName: string,
  //   operationalField: string | null,
  //   image: string
  // }[]) => {
  //   if (!!array && !!array.length) {
  //     const newArray = array.map((item: {
  //       id: string | number,
  //       completeName: string,
  //       operationalField: string | null,
  //       image: string
  //     }) => {
  //       return item.completeName;
  //     } );
  //     return newArray.join();
  //   } else {
  //     return "-";
  //   }
  // };

  const oldestSession = sessions?.reduce<SessionEdition | null>((min, s) => {
    if (!min) return s;
    return Number(s.start_date) > Number(min.start_date) ? s : min;
  }, null) ?? null;

  const oldestSessionString = oldestSession?.rooms && oldestSession?.rooms.length > 0 ? oldestSession?.rooms.toString().replaceAll(","," - ") : null;


  const getNextSession = () => {
    const today = new Date().getTime();
    if (!nextSession) return;
    const start = new Date(nextSession.start_date);
    const endNumber = nextSession.end_date;

    if (Number(today) > Number(endNumber)) {
      return "";
    }

    return `${formatDate(new Date(start), i18n.language)?.replaceAll("/","-")} ${getHours(start) < 10 ? `0${getHours(start)}` : getHours(start)}:${getMinutes(start) < 10 ? `${getMinutes(start)}0` : getMinutes(start)}`;
  };

  const getUrlSession = () => {
    if (!nextSession) return;

    return nextSession.url || null;
  };

  useEffect(() => {
    const now = Date.now();
    const orderedSessions = sessions?.sort((a, b) => a.id - b.id);

    const currentSession = orderedSessions?.find(
      session => now >= Number(session.start_date) && now <= Number(session.end_date)
    );

    const isNext = orderedSessions?.find(
      session => now <= Number(session.start_date)
    );

    const lastEdition: SessionEdition | null = orderedSessions?.reduce((max, obj) => (obj.id > max.id ? obj : max), orderedSessions[0]) || null;

    if (currentSession) {
      setNextSession(currentSession);
      setIsCurrentSession(true);
    } else if (isNext) {
      setNextSession(isNext);
      setIsCurrentSession(false);
    } else {
      setNextSession(lastEdition);
      setIsCurrentSession(false);
    }
  }, [sessions]);

  const getEditionTeachers = () => {
    const teacherNamesSet = new Set();

    sessions?.forEach(item => {
      item.teachers.forEach(teacher => {
        if (teacher.completeName) {
          teacherNamesSet.add(teacher.completeName);
        }
      });
    });

    return Array.from(teacherNamesSet).join(",");
  };

  const handleUnsubscribeSuccess = () => {
    // resetEnrollmentFlowState();
    invalidateEditions();
    invalidateLearningObject();
  };

  return (
    <>

      <CardGrid
        container
        $booked={ booked }
        { ...props }
      >
        <CardBodyGrid
          container
          item
          $booked={ booked }
          $currentDay={ currentDay }
          gap="1rem"
          width="50%"
        >
          {
            startEdition === endEdition ?
              <InfoTypography fontWeight={ 700 }>
                <Icon
                  icon="Icons_scadenza-e-edizioni"
                  size="20px"
                />
                { /* { formatDate(date, i18n.language) } */ }
                { startEdition }
              </InfoTypography> :
              <InfoTypography fontWeight={ 700 }>
                <Icon
                  icon="Icons_scadenza-e-edizioni"
                  size="20px"
                />
                { t("from") + " " +  startEdition + " " + t("to") + " " + endEdition }

              </InfoTypography>
          }
          { getNextSession() ? (
            <InfoTypography className="location">
              <Icon
                icon={ learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS ? "Icons_meeting" : "Icons_location" }
                size="20px"
              />
              {
                learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS ? (
                  <Link
                    href={ getUrlSession() || "" }
                    target={ "_blank" }
                    style={ {
                      pointerEvents:!currentDay? "none" : "auto"
                    } }
                  >
                    { getUrlSession() ? t("meeting_online") : t("meeting_online_not_defined") }
                  </Link>
                ) : (
                  nextSession?.gmaps_url ? (
                    <p>
                      { isCurrentSession ? t("current-session") : t("next-session") }:
                      <Link
                        href={ nextSession?.gmaps_url }
                        target={ "_blank" }
                        style={ {
                          textDecoration:"underline"
                        } }
                      >
                        { nextSession?.location + " " }
                      </Link>
                      { roomsString
                        ?  ` - ${ t("room") } : ${ roomsString }`
                        : ""
                      }
                    </p>
                  ) : (
                    <p>
                      { nextSession?.location }{ roomsString
                        ?  ` - ${ t("room") } : ${ roomsString }`
                        : ""
                      }
                    </p>
                  )
                )
              }
            </InfoTypography>
          ) : (
            <InfoTypography className="location">
              <Icon
                icon={ learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS ? "Icons_meeting" : "Icons_location" }
                size="20px"
              />
              { learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS
                ? t("meeting_online")
                : (
                  oldestSession?.gmaps_url ? (
                    <p>
                      { t("last-session") }:
                      <Link
                        href={ oldestSession?.gmaps_url }
                        target={ "_blank" }
                        style={ {
                          textDecoration:"underline"
                        } }
                      >
                        { oldestSession?.location + " " }
                      </Link>
                      { oldestSessionString
                        ?  ` - ${ t("room") } : ${ oldestSessionString }`
                        : ""
                      }
                    </p>
                  ) : (
                    <p>
                      { nextSession?.location }{ roomsString
                        ?  ` - ${ t("room") } : ${ roomsString }`
                        : ""
                      }
                    </p>
                  )
                )
              }
            </InfoTypography>
          ) }
          { !!getNextSession() && (
            <InfoTypography className="time">
              <Icon
                icon="Icons_time"
                size="20px"
              />
              { isCurrentSession ? t("current-session") : t("next-session") }: { getNextSession() }
            </InfoTypography>
          ) }
          {
            !!getEditionTeachers() && (
              <InfoTypography className="teachers">
                <Icon icon="Icons_Profilo"
                  size="20px"/>
                { `${getEditionTeachers()}` }
              </InfoTypography>
            )
          }

        </CardBodyGrid>
        <div className="separator"  ></div>
        <CardBodyGrid
          item
          container
          alignItems="center"
          $booked={ booked }
          $currentDay={ currentDay }
          gap={ theme.spacing(1) }
          width="40%"
        >
          <StyledBoxContact $visible={ bannerVisible }
            sx={ {
              left:"unset",
              right:0,
              top:0
            } }>
            <Typography
              display={ "flex" }
              fontWeight= { 500 }
              color= { theme.customColors.textInput }
              maxWidth={ "calc(100% - 44px)" }
            >
              { bannerText }
            </Typography>
            <Icon
              icon="close"
              size={ 24 }
              color= { theme.palette.primary?.main }
              onClick={ ()=> { setBannerVisible(false) } }
            />
          </StyledBoxContact>

          <Box>
            <SeatsTypography component="span">{ bookedSeats || 0 }</SeatsTypography>
            <SeatsTypography
              component="span"
              fontWeight={ 600 }
            > / </SeatsTypography>
            <SeatsTypography component="span" fontWeight={ 700 }>{ maxSeats }</SeatsTypography>
          </Box>
          <InfoTypography color={ theme.customColors.textWarning }>
            <Icon
              color={ theme.customColors.textWarning }
              icon={ "Icons_obbligatorio" }
              size="20px"

            />
            { t("places_available", { count:maxSeats - bookedSeats }) }
          </InfoTypography>

          <ButtonDigited
            typology={ "secondary" }
            text={ getButtonLabel().replace("|", "\n") }
            sx={ {
              textAlign: "center",
              whiteSpace: "pre-line",
              [theme.breakpoints.down("sm")]:{
                display: "inline-block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                width: "129px"
              }
            } }
            disabled={
              // isRejected
              // || !isBookable
              (!seatsAvailable && !hasEnrollableEditions)
              || enrollmentEditionIsLoading
              || showInProgressStatus
              || isAnyPolling
              || (statusEdition as StatusLO) === STATUS_LO.W
              || ((statusEdition as StatusLO) !== STATUS_LO.W && hasAnyEditionPendingApproval)
              || isAbsent
              || Boolean(editionId && isEditionPendingApproval(editionId, userId))
              || (enrollType === "TOP_DOWN" || enrollType === "TOP_MANDATORY" || currentLO?.enrollmentEnrollType.includes("TOP")) && statusEdition === STATUS_LO.E
              || isCompleted
              || isPollingEditionChange
              || isUnsubscribing
            }
            onClick={ ()=> {
              if(statusEdition === STATUS_LO.E && !hasEnrollableEditions) {
                setBannerText(t("no_enrollable_editions"));
                setBannerVisible(true);
              } else if(statusEdition === STATUS_LO.E && onChangeEditionClick) {
                onChangeEditionClick();
              } else if(!booked || hasEditionEnrolledAbsent){
                if(isBookable && (enrollType === "REQUESTED_AUTO_ENROLL" || enrollType === "AUTO_ENROLL" || selfEnrollment)) {
                  addEnrollmentMutation();
                }
                else {
                  setBannerText(t("contact_text"));
                  setBannerVisible(true);
                }
              }
            } }/>
          { booked && statusEdition === STATUS_LO.E && (enrollType === "AUTO_ENROLL" || enrollType === "REQUESTED_AUTO_ENROLL") && (
            <ButtonDigited
              typology={ "secondary" }
              text={ isUnsubscribing ? t("unsubscribing") : t("unsubscribe") }
              onClick={ () => setShowUnsubscribeConfirm(true) }
              sx={ {
                [theme.breakpoints.down("sm")]:{
                  display: "inline-block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "129px"
                }
              } }
              disabled={ isUnsubscribing || isAnyPolling || showInProgressStatus || isPollingEditionChange }
            />
          ) }
          { sessions && sessions.length > 0 && (
            <ButtonDigited
              typology={ "primary" }
              text={ t("show-sessions") }
              sx={ {
                [theme.breakpoints.down("sm")]:{
                  display: "inline-block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "129px"
                }
              } }
              onClick={ ()=> setShowSessionDetails(true) } />
          ) }


        </CardBodyGrid>
      </CardGrid>
      <Modal
        open={ showModal }
        onClose={ ()=> {
          setShowModal(false);
          invalidateEditions();
        } }
        description={ modalText }
        title={ t("attention") }
      />
      <Modal
        open={ showModalEditionEnrollSuccess }
        onClose={ ()=> {
          setShowModalEditionEnrollSuccess(false);
          if (enrollType === "REQUESTED_AUTO_ENROLL") {
            // Enrollment with approval: invalidate on modal close
            invalidateEditions();
            invalidateLearningObject();
          }
          // For AUTO_ENROLL: do NOT call invalidateLearningObject() and invalidateEditions()
          // They will be called by polling when complete
        } }
        description={ t("enrollment-edition-success") }
        title={ t("editions") }
      />
      <Modal
        open={ showModalPollingResult }
        onClose={ ()=> {
          setShowModalPollingResult(false);
          invalidateEditions();
          invalidateLearningObject();
        } }
        description={ pollingResultMessage }
        title={ t("enrollment-status") }
      />
      <UnsubscribeEditionConfirmModal
        editionId={ editionId }
        objectId={ id }
        open={ showUnsubscribeConfirm }
        onClose={ () => setShowUnsubscribeConfirm(false) }
        onSuccess={ handleUnsubscribeSuccess }
        onUnsubscribingChange={ setIsUnsubscribing }
        title={ t("attention") }
        description={ t("unsubscribe_edition_confirm_description") }
      />
      <ModalSessionDetail
        open={ showSessionDetails }
        handleClose={ () => setShowSessionDetails(false) }
        title={ t("sessions-details") }
        extraInfo={ extraInfo }
        data={ sessions ? [...sessions].sort((a, b) => Number(a.start_date) - Number(b.start_date)) : [] }
        isVirtual={ learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS }
      />
    </>

  );
}
